/**
 * 设备命令构造器
 * 对齐旧版 js/server-1.js 中的命令协议
 *
 * 协议格式: AA 53 LENGTH COMMAND DATA... CHECKSUM
 * - 帧头: AA 53
 * - 长度: 从命令字到校验和的字节数
 * - 命令字: 1字节
 * - 数据: 变长
 * - 校验和: 帧头+长度+命令+数据的和，取反+1，取低8位
 */

import { hexStringToBytes } from './protocol'

/**
 * 设备命令代码
 * 与原项目 js/server-1.js 中的命令代码保持一致
 */
export enum DeviceCommand {
  // Status commands
  GET_STATUS = 0x80,
  GET_DEVICE_DETAIL = 0x82,

  // EQ commands (module 0x52)
  EQ_MODULE = 0x52,
  EQ_SUBMODE_SINGLE = 0x02,  // 单点设置
  EQ_SUBMODE_LOGIN_STATUS = 0x13, // EQ登录状态同步（新增）
  EQ_SUBMODE_MODE = 0x14, // EQ开关、模式、标签及一键吃鸡联动状态
  EQ_SUBMODE_SEGMENT1 = 0x15, // 分段设置 - 第1段 (0-3频点)
  EQ_SUBMODE_SEGMENT2 = 0x16, // 分段设置 - 第2段 (4-7频点)
  EQ_SUBMODE_SEGMENT3 = 0x17, // 分段设置 - 第3段 (8-11频点)
  EQ_SUBMODE_SEGMENT4 = 0x18, // 分段设置 - 第4段 (12-13频点)

  // Mute commands
  SET_SPEAKER_MUTE = 0x54,  // 喇叭静音
  SET_MIC_MUTE = 0x55,      // 麦克风静音

  // Volume commands
  SET_SPEAKER_VOLUME = 0x56, // 喇叭音量
  SET_MIC_VOLUME = 0x57,       // 麦克风音量

  // Other feature commands
  SET_ANTI_INTERFERENCE = 0x58, // 抗干扰
  SET_ENC = 0x51,               // AI降噪 (ENC)
  SET_RGB = 0x60,               // RGB灯效
  SET_VOICE_EFFECT = 0x61,      // 魔幻音效

  // Power control
  SET_POWER = 0x70,             // 关机/开机

  // Sidetone/Audio mix commands (0x5B 系列)
  SIDETONE_BASE = 0x5B,          // 耳返/音频混合基础命令
  SIDETONE_SUBMODE_BALANCE = 0x00,   // 子命令: 音频混合比例
  SIDETONE_SUBMODE_MODE = 0x01,      // 子命令: 音频模式切换 (Mix/Split/Adaptive)
  SIDETONE_SUBMODE_VOLUME = 0x02,    // 子命令: 耳返音量
  SIDETONE_SUBMODE_SWITCH = 0x03,    // 子命令: 耳返开关
  BT_SHARE_SUBMODE_SWITCH = 0x04,    // 子命令: 蓝牙音频分享
  CALL_BRIDGE_SUBMODE_SWITCH = 0x05, // 子命令: 多方通话桥接
}

// Frame header (AA 53)
const FRAME_HEADER: [number, number] = [0xAA, 0x53]

/**
 * Calculate checksum for protocol frame
 * Algorithm: Sum of header + all data bytes, then 16-bit complement
 */
export function calculateChecksum(data: number[]): string {
  let sum = FRAME_HEADER[0] + FRAME_HEADER[1]

  for (const byte of data) {
    const value = typeof byte === 'string' ? parseInt(byte, 16) : byte
    if (!isNaN(value) && value >= 0 && value <= 0xFF) {
      sum += value
    }
  }

  const sum16bit = sum & 0xFFFF
  const checksum = (~sum16bit + 1) & 0xFF

  return checksum.toString(16).padStart(2, '0').toUpperCase()
}

/**
 * Build complete command frame
 */
function buildFrame(command: number, data: number[] = []): Uint8Array {
  // 对齐旧版: LENGTH 等于整帧字节长度 (AA 53 LENGTH CMD DATA... CHECKSUM)
  // 例如 mute: AA 53 08 54 00 01 01 xx (总长 8)
  const length = data.length + 5 // header(2) + length(1) + command(1) + data(n) + checksum(1)
  const frameData = [length, command, ...data]
  const checksum = calculateChecksum(frameData)
  const checksumValue = parseInt(checksum, 16)

  return new Uint8Array([...FRAME_HEADER, ...frameData, checksumValue])
}

/**
 * Convert float32 to 4 bytes (little-endian)
 * Used for EQ frequency, gain, Q value encoding
 */
function float32ToBytes(value: number): number[] {
  const buffer = new ArrayBuffer(4)
  const view = new DataView(buffer)
  view.setFloat32(0, value, true) // true = little-endian
  return Array.from(new Uint8Array(buffer))
}

/**
 * Build speaker mute command
 * 开: AA 53 08 54 00 01 01 a5
 * 关: AA 53 08 54 00 01 00 a6
 */
export function buildSpeakerMuteCommand(mute: boolean): Uint8Array {
  return buildFrame(DeviceCommand.SET_SPEAKER_MUTE, [0x00, 0x01, mute ? 0x01 : 0x00])
}

/**
 * Build microphone mute command
 * 开: AA 53 08 55 00 01 01 a4
 * 关: AA 53 08 55 00 01 00 a5
 */
export function buildMicMuteCommand(mute: boolean): Uint8Array {
  return buildFrame(DeviceCommand.SET_MIC_MUTE, [0x00, 0x01, mute ? 0x01 : 0x00])
}

/**
 * Build speaker volume command
 * Original: sendSliderValue with cmd2='56'
 * Format: AA 53 08 56 00 01 [volume_value] [checksum]
 * Volume range: 0-100
 */
export function buildSpeakerVolumeCommand(volume: number): Uint8Array {
  const clampedVolume = Math.max(0, Math.min(100, Math.round(volume)))
  return buildFrame(DeviceCommand.SET_SPEAKER_VOLUME, [0x00, 0x01, clampedVolume])
}

/**
 * Build microphone volume command
 * Original: sendSliderValue with cmd2='57'
 * Format: AA 53 08 57 00 01 [volume_value] [checksum]
 * Volume range: 0-100
 */
export function buildMicVolumeCommand(volume: number): Uint8Array {
  const clampedVolume = Math.max(0, Math.min(100, Math.round(volume)))
  return buildFrame(DeviceCommand.SET_MIC_VOLUME, [0x00, 0x01, clampedVolume])
}

/**
 * Build anti-interference command
 * 开: AA 53 08 58 00 01 01 a1
 * 关: AA 53 08 58 00 01 00 a2
 */
export function buildAntiInterferenceCommand(enabled: boolean): Uint8Array {
  return buildFrame(DeviceCommand.SET_ANTI_INTERFERENCE, [0x00, 0x01, enabled ? 0x01 : 0x00])
}

/**
 * Build ENC (AI降噪) command
 * Original from server-1.js: AA 53 09 51 00 02 [on/off] 0{level} [checksum]
 * Level range: 1-6 (旧版是1-6档，Vue3 UI需要对齐)
 *
 * @param enabled - 开关状态
 * @param level - 档位 1-6
 */
export function buildEncCommand(enabled: boolean, level: number): Uint8Array {
  const clampedLevel = Math.max(1, Math.min(6, Math.round(level)))
  // 格式: length=09, cmd=51, 00, 02, [on/off], [level], checksum
  return buildFrame(DeviceCommand.SET_ENC, [0x00, 0x02, enabled ? 0x01 : 0x00, clampedLevel])
}

/**
 * Build RGB light effect command
 * Original: AA 53 08 60 01 01 [mode] [brightness]
 * Simplified version - basic on/off with color
 */
export function buildRgbCommand(enabled: boolean, mode: number = 0): Uint8Array {
  // 对齐旧版 server-1.js: AA 53 08 60 01 01 [mode] [checksum]
  // 备注: 旧版协议该命令不带 brightness 字节
  return buildFrame(DeviceCommand.SET_RGB, [0x01, 0x01, enabled ? mode : 0x00])
}

/**
 * Build power on/off command
 * 关机: AA 53 08 70 00 01 00 8A
 * 开机: AA 53 08 70 00 01 01 89
 */
export function buildPowerCommand(powerOn: boolean): Uint8Array {
  return buildFrame(DeviceCommand.SET_POWER, [0x00, 0x01, powerOn ? 0x01 : 0x00])
}

/**
 * Build voice effect (魔幻音效) command
 * Types: 0=normal, 1=boy, 2=girl, 3=child, 4=robot, 5=monster
 */
export function buildVoiceEffectCommand(type: number): Uint8Array {
  return buildFrame(DeviceCommand.SET_VOICE_EFFECT, [0x00, 0x01, type])
}

/**
 * Build sidetone switch command (耳返开关)
 * 开启: AA 53 08 5B 03 01 01 [checksum]
 * 关闭: AA 53 08 5B 03 01 00 [checksum]
 */
export function buildSidetoneSwitchCommand(enabled: boolean): Uint8Array {
  return buildFrame(DeviceCommand.SIDETONE_BASE, [
    DeviceCommand.SIDETONE_SUBMODE_SWITCH,
    0x01,
    enabled ? 0x01 : 0x00
  ])
}

/**
 * Build sidetone volume command (耳返音量)
 * 格式: AA 53 08 5B 02 01 [volume:0-100] [checksum]
 */
export function buildSidetoneVolumeCommand(volume: number): Uint8Array {
  const clampedVolume = Math.max(0, Math.min(100, Math.round(volume)))
  return buildFrame(DeviceCommand.SIDETONE_BASE, [
    DeviceCommand.SIDETONE_SUBMODE_VOLUME,
    0x01,
    clampedVolume
  ])
}

/**
 * Build audio mode command (音频模式切换: Mix/Split/Adaptive)
 * 格式: AA 53 08 5B 01 01 [mode] [checksum]
 * 无应答命令
 */
export function buildAudioModeCommand(mode: number): Uint8Array {
  return buildFrame(DeviceCommand.SIDETONE_BASE, [
    DeviceCommand.SIDETONE_SUBMODE_MODE,
    0x01,
    mode
  ])
}

/**
 * Build audio balance command (音频混合比例)
 * 格式: AA 53 08 5B 00 01 [value] [checksum]
 * Position value: 0-100 (UI), mapped to 0-200 (protocol)
 * 最左 => 0, 中间 => 100, 最右 => 200
 */
export function buildAudioBalanceCommand(positionValue: number): Uint8Array {
  const sendValue = Math.max(0, Math.min(200, Math.round(positionValue * 2)))
  return buildFrame(DeviceCommand.SIDETONE_BASE, [
    DeviceCommand.SIDETONE_SUBMODE_BALANCE,
    0x01,
    sendValue
  ])
}

/** Positioning uses the same EQ payload but reserves 0x15/0x16 for footsteps
 * and 0x17/0x18 for gunshots (legacy chijicode.js mapping). */
export function buildPositioningSegmentCommand(
  subModule: number,
  points: EqDataPoint[],
): Uint8Array {
  if (![0x15, 0x16, 0x17, 0x18].includes(subModule)) {
    throw new Error(`Invalid positioning submodule: ${subModule}`)
  }
  const payload: number[] = []
  for (const point of points) {
    const freqValue = parseFloat(point.freq.replace(/[HzK]/gi, '')) * (point.freq.includes('K') ? 1000 : 1)
    payload.push(...float32ToBytes(freqValue))
    payload.push(...float32ToBytes(point.gain))
    payload.push(...float32ToBytes(parseFloat(point.q)))
  }
  // Legacy zyg_build_packet includes the payload length after the submodule.
  const totalLength = payload.length + 7
  const frameData = [totalLength, DeviceCommand.EQ_MODULE, subModule, payload.length, ...payload]
  const checksum = parseInt(calculateChecksum(frameData), 16)
  return new Uint8Array([0xAA, 0x53, ...frameData, checksum])
}

export function buildPositioningCurveCommands(
  footsteps: EqDataPoint[],
  gunshots: EqDataPoint[],
): Uint8Array[] {
  return [
    buildPositioningSegmentCommand(0x15, footsteps.slice(0, 4)),
    buildPositioningSegmentCommand(0x16, footsteps.slice(4)),
    buildPositioningSegmentCommand(0x17, gunshots.slice(0, 4)),
    buildPositioningSegmentCommand(0x18, gunshots.slice(4)),
  ]
}
/** 2.4G / Bluetooth audio sharing (legacy protocol: 0x5B / 0x04). */
export function buildBtShareSwitchCommand(enabled: boolean): Uint8Array {
  return buildFrame(DeviceCommand.SIDETONE_BASE, [
    DeviceCommand.BT_SHARE_SUBMODE_SWITCH,
    0x01,
    enabled ? 0x01 : 0x00,
  ])
}

/** Multi-device call bridge (legacy protocol: 0x5B / 0x05). */
export function buildCallBridgeSwitchCommand(enabled: boolean): Uint8Array {
  return buildFrame(DeviceCommand.SIDETONE_BASE, [
    DeviceCommand.CALL_BRIDGE_SUBMODE_SWITCH,
    0x01,
    enabled ? 0x01 : 0x00,
  ])
}

/**
 * Build EQ login status packet (EQ登录状态同步)
 * 格式: AA 53 09 52 13 02 [loginStatus:0/1] 00 [checksum]
 * 每次连续下发 0x52 EQ 数据前，先通过该协议同步当前登录状态
 */
export function buildEqLoginStatusCommand(isLoggedIn: boolean): Uint8Array {
  const loginStatus = isLoggedIn ? 0x01 : 0x00
  return buildFrame(DeviceCommand.EQ_MODULE, [
    DeviceCommand.EQ_SUBMODE_LOGIN_STATUS,
    0x02,  // data length
    loginStatus,
    0x00   // reserved
  ])
}

// ============ EQ Commands (Complex Protocol) ============

/**
 * EQ Data Point - 单个频点数据
 * Aligned with original eq_setting.js structure
 */
export interface EqDataPoint {
  fid: number      // 频点ID (1-14)
  freq: string     // 频率显示文本 (如 '25HZ', '1K')
  gain: number     // 增益值 (-12 到 +12 dB)
  q: string        // Q值 (如 '0.75')
}

/**
 * Build single-point EQ command (实时单点调节)
 * Module 0x52, SubMode 0x02 + bandIndex
 * Format for single point: uses float32 little-endian
 *
 * @param bandIndex - 频段索引 (0-13)
 * @param point - EQ数据点
 */
export function buildEqSinglePointCommand(bandIndex: number, point: EqDataPoint): Uint8Array {
  const subModule = DeviceCommand.EQ_SUBMODE_SINGLE
  const freqValue = parseFloat(point.freq.replace(/[HzK]/gi, '')) * (point.freq.includes('K') ? 1000 : 1)
  const qValue = parseFloat(point.q)

  // Data: subModule(1) + bandIndex(1) + freq(4 bytes float32) + gain(4 bytes float32) + q(4 bytes float32)
  const data = [
    subModule,
    bandIndex,
    ...float32ToBytes(freqValue),
    ...float32ToBytes(point.gain),
    ...float32ToBytes(qValue),
  ]

  return buildFrame(DeviceCommand.EQ_MODULE, data)
}

/**
 * Build segment EQ command (分段发送 - 完整曲线)
 * 14段按4个segment分段发送: 0x15/0x16/0x17/0x18
 *
 * Segment mapping:
 * - 0x15: indices 0-3 (频点 0,1,2,3)
 * - 0x16: indices 4-7 (频点 4,5,6,7)
 * - 0x17: indices 8-11 (频点 8,9,10,11)
 * - 0x18: indices 12-13 (频点 12,13)
 *
 * @param segmentIndex - 段索引 (0-3)
 * @param points - 该段的所有频点数据
 */
export function buildEqSegmentCommand(segmentIndex: number, points: EqDataPoint[]): Uint8Array {
  const subModules = [
    DeviceCommand.EQ_SUBMODE_SEGMENT1,
    DeviceCommand.EQ_SUBMODE_SEGMENT2,
    DeviceCommand.EQ_SUBMODE_SEGMENT3,
    DeviceCommand.EQ_SUBMODE_SEGMENT4,
  ]

  const subModule = subModules[segmentIndex]
  if (subModule === undefined) {
    throw new Error(`Invalid segment index: ${segmentIndex}`)
  }

  // Data: subModule(1) + [freq(4) + gain(4) + q(4)] * pointCount
  const data: number[] = [subModule]

  for (const point of points) {
    const freqValue = parseFloat(point.freq.replace(/[HzK]/gi, '')) * (point.freq.includes('K') ? 1000 : 1)
    const qValue = parseFloat(point.q)
    data.push(...float32ToBytes(freqValue))
    data.push(...float32ToBytes(point.gain))
    data.push(...float32ToBytes(qValue))
  }

  return buildFrame(DeviceCommand.EQ_MODULE, data)
}

/**
 * Build complete EQ curve commands (all 4 segments)
 * Returns array of 4 commands that should be sent sequentially with delays
 */
export function buildEqCurveCommands(allPoints: EqDataPoint[]): Uint8Array[] {
  // Split 14 points into 4 segments
  const segments = [
    allPoints.slice(0, 4),   // indices 0-3
    allPoints.slice(4, 8),   // indices 4-7
    allPoints.slice(8, 12),  // indices 8-11
    allPoints.slice(12, 14), // indices 12-13
  ]

  return segments.map((points, index) => buildEqSegmentCommand(index, points))
}

/**
 * Build EQ switch/mode/tag command.
 * AA 53 0C 52 14 05 B0 B1 B2 B3 B4 checksum
 * B0 EQ enabled, B1 EQ mode, B2 positioning enabled,
 * B3 EQ preset index, B4 positioning preset index.
 */
export function buildEqModeCommand(
  enabled: boolean,
  mode: number,
  presetIndex = 0,
  positioningEnabled = false,
  positioningPresetIndex = 0,
): Uint8Array {
  const normalizedMode = Math.max(0, Math.min(2, Math.round(mode)))
  const normalizedPresetIndex = enabled
    ? Math.max(0, Math.min(31, Math.round(presetIndex)))
    : 0

  return buildFrame(DeviceCommand.EQ_MODULE, [
    DeviceCommand.EQ_SUBMODE_MODE,
    0x05,
    enabled ? 0x01 : 0x00,
    normalizedMode,
    positioningEnabled ? 0x01 : 0x00,
    normalizedPresetIndex,
    Math.max(0, Math.min(31, Math.round(positioningPresetIndex))),
  ])
}

/** Backward-compatible helper for callers that only toggle EQ. */
export function buildEqEnableCommand(enabled: boolean): Uint8Array {
  return buildEqModeCommand(enabled, 0)
}

// ============ Command Presets for Direct Use ============

export const COMMAND_PRESETS = {
  // Speaker mute
  SPEAKER_MUTE_ON: 'AA 53 08 54 00 01 01 A5',
  SPEAKER_MUTE_OFF: 'AA 53 08 54 00 01 00 A6',

  // Mic mute
  MIC_MUTE_ON: 'AA 53 08 55 00 01 01 A4',
  MIC_MUTE_OFF: 'AA 53 08 55 00 01 00 A5',

  // Anti-interference
  ANTI_INTERFERENCE_ON: 'AA 53 08 58 00 01 01 A1',
  ANTI_INTERFERENCE_OFF: 'AA 53 08 58 00 01 00 A2',

  // Status queries
  GET_STATUS: 'AA 53 08 80 00 01 00 7A',
  GET_DEVICE_DETAIL: 'AA 53 08 82 00 01 00 78',
} as const

/**
 * Parse command hex string to Uint8Array
 */
export function parseCommandHex(hexString: string): Uint8Array {
  return hexStringToBytes(hexString)
}

/**
 * Validate command format
 */
export function validateCommand(data: Uint8Array): boolean {
  if (data.length < 8) return false
  if (data[0] !== FRAME_HEADER[0] || data[1] !== FRAME_HEADER[1]) return false

  // Verify checksum
  const payload = Array.from(data.slice(2, -1))
  const receivedChecksum = data[data.length - 1]
  const calculatedChecksum = parseInt(calculateChecksum(payload), 16)

  return receivedChecksum === calculatedChecksum
}

// ============ EQ Login Status Helper Functions ============

/**
 * 判断命令是否为 EQ 数据命令（需要在发送前注入登录状态）
 * @param hexString - 十六进制命令字符串（如 "AA 53 0E 52 02..."）
 * @returns true 如果是 EQ 数据命令（module=0x52 且 subModule!=0x13）
 */
export function isEqDataCommand(hexString: string): boolean {
  const normalized = String(hexString || '').replace(/\s+/g, '').toUpperCase()
  if (normalized.length < 10) return false

  const module = parseInt(normalized.slice(6, 8), 16)
  const subModule = parseInt(normalized.slice(8, 10), 16)

  return module === DeviceCommand.EQ_MODULE
    && subModule !== DeviceCommand.EQ_SUBMODE_LOGIN_STATUS
}

/**
 * 判断命令是否为 RGB 命令
 * @param hexString - 十六进制命令字符串
 * @returns true 如果是 RGB 命令（0x60）
 */
export function isRgbCommand(hexString: string): boolean {
  const normalized = String(hexString || '').replace(/\s+/g, '').toUpperCase()
  if (normalized.length < 8) return false

  const command = parseInt(normalized.slice(6, 8), 16)
  return command === DeviceCommand.SET_RGB
}

/**
 * 将 Uint8Array 转换为十六进制字符串（用于日志和判断）
 * @param bytes - 字节数组
 * @returns 十六进制字符串（空格分隔，大写）
 */
export function bytesToHexString(bytes: Uint8Array | number[]): string {
  return Array.from(bytes)
    .map(b => b.toString(16).padStart(2, '0').toUpperCase())
    .join(' ')
}

/**
 * EQ 登录状态同步控制器
 * 用于避免在同一连续 EQ 下发过程中重复发送登录状态
 */
export class EqLoginStatusSyncController {
  private lastDevice: any = null
  private lastStatus: number | null = null
  private lastTime: number = 0
  private readonly syncWindowMs: number = 1000 // 1秒内同一设备不重复发送

  /**
   * 判断是否需要同步 EQ 登录状态
   */
  shouldSync(device: any, loginStatus: number): boolean {
    const now = Date.now()
    return (
      this.lastDevice !== device ||
      this.lastStatus !== loginStatus ||
      now - this.lastTime >= this.syncWindowMs
    )
  }

  /**
   * 标记已同步
   */
  markSynced(device: any, loginStatus: number): void {
    this.lastDevice = device
    this.lastStatus = loginStatus
    this.lastTime = Date.now()
  }

  /**
   * 重置同步状态（设备断开时调用）
   */
  reset(): void {
    this.lastDevice = null
    this.lastStatus = null
    this.lastTime = 0
  }
}
